import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Landmark, ChevronDown, AlertCircle } from 'lucide-react';
import { getBanks } from 'nigeria-banks-list';
import ModalWrapper from '../ModalWrapper';

const currencies = ['USD', 'EUR', 'CAD', 'NGN', 'GBP', 'JPY'];

// Bank account number lengths for Nigerian banks
const getBankAccountLength = (bankName) => {
    const bankLengths = {
        // Commercial Banks
        'Access Bank': 10,
        'Zenith Bank': 10,
        'Guaranty Trust Bank': 10,
        'GTBank': 10,
        'First Bank of Nigeria': 10,
        'United Bank for Africa': 10,
        'UBA': 10,
        'Union Bank of Nigeria': 10,
        'Fidelity Bank': 10,
        'Stanbic IBTC Bank': 10,
        'First City Monument Bank': 10,
        'Sterling Bank': 10,
        'Wema Bank': 10,
        'Unity Bank': 10,
        'Providus Bank': 10,
        'Suntrust Bank': 10,
        'Titan Trust Bank': 10,
        
        // Microfinance Banks
        'VFD Microfinance Bank': 10,
        'Sparkle Microfinance Bank': 10,
        
        // Digital Banks
        'Kuda Bank': 10,
        'OPay': 10,
        'PalmPay': 10,
        'Mintyn': 10,
        'Rubies Bank': 10,
        '9mobile': 10,
        
        // Default length
        'default': 10
    };
    
    return bankLengths[bankName] || bankLengths.default;
};

export default function WithdrawalModal({ balance, onClose, onComplete }) {
    const [method, setMethod] = useState('bank');
    const [amount, setAmount] = useState('0.00');
    const [currency, setCurrency] = useState('USD');
    const [accountName, setAccountName] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [bank, setBank] = useState('');
    const [banks, setBanks] = useState([]);
    const [showBankDropdown, setShowBankDropdown] = useState(false);
    const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [accountNameError, setAccountNameError] = useState('');
    const [accountNumberError, setAccountNumberError] = useState('');

    const minWithdrawal = 50;
    const availableFunds = balance;
    const NGN_PER_USD = 1500;
    const numericAmount = parseFloat(amount) || 0;
    const nairaEquivalent = numericAmount * NGN_PER_USD;

    // Load banks when component mounts
    useEffect(() => {
        const allBanks = getBanks();
        setBanks(allBanks);
    }, []);

    // Filter banks based on search
    const filteredBanks = banks.filter(b => 
        b.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Validate account name
    const validateAccountName = (name) => {
        if (name && /[0-9]/.test(name)) {
            setAccountNameError('Account name should not contain numbers');
            return false;
        } else if (name && name.length < 3) {
            setAccountNameError('Account name must be at least 3 characters');
            return false;
        } else {
            setAccountNameError('');
            return true;
        }
    };

    // Validate account number
    const validateAccountNumber = (number, bankName) => {
        const expectedLength = getBankAccountLength(bankName);
        
        if (number && number.length !== expectedLength) {
            setAccountNumberError(`Account number for ${bankName || 'this bank'} should be ${expectedLength} digits`);
            return false;
        } else if (number && !/^\d+$/.test(number)) {
            setAccountNumberError('Account number must contain only digits');
            return false;
        } else {
            setAccountNumberError('');
            return true;
        }
    };

    // Handle account name change
    const handleAccountNameChange = (e) => {
        const value = e.target.value;
        // Only allow letters, spaces, dots, and hyphens
        const isValid = /^[A-Za-z\s\.\-]*$/.test(value);
        if (isValid) {
            setAccountName(value);
            validateAccountName(value);
        }
    };

    // Handle account number change
    const handleAccountNumberChange = (e) => {
        const value = e.target.value;
        // Only allow numbers
        const isValid = /^\d*$/.test(value);
        const maxLength = getBankAccountLength(bank);
        
        if (isValid && value.length <= maxLength) {
            setAccountNumber(value);
            if (value.length === maxLength) {
                validateAccountNumber(value, bank);
            } else if (accountNumberError) {
                setAccountNumberError('');
            }
        }
    };

    // Validate when bank is selected
    useEffect(() => {
        if (accountNumber && bank) {
            validateAccountNumber(accountNumber, bank);
        }
    }, [bank, accountNumber]);

    return (
        <ModalWrapper onClose={onClose} title="Bank Transfer Withdrawal Request">
            <div className="p-4 sm:p-6">
                {/* Method Tabs */}
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                    <button
                        onClick={() => setMethod('bank')}
                        className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all w-full sm:w-auto ${
                            method === 'bank' 
                                ? 'bg-[#c9a227] text-black' 
                                : 'bg-[#4a4a4a] text-white hover:bg-[#5a5a5a]'
                        }`}
                    >
                        <Landmark className="w-4 h-4" />
                        <span className="text-sm sm:text-base">Online Bank Transfer</span>
                    </button>
                    <button
                        onClick={() => setMethod('paypal')}
                        className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all w-full sm:w-auto ${
                            method === 'paypal' 
                                ? 'bg-[#c9a227] text-black' 
                                : 'bg-[#4a4a4a] text-white hover:bg-[#5a5a5a]'
                        }`}
                    >
                        <span className="font-bold text-sm">P</span>
                        <span className="text-sm sm:text-base">Paypal</span>
                    </button>
                </div>

                {/* Amount Input */}
                <div className="bg-[#4a4a4a] rounded-lg p-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-2">
                    <div className="relative sm:w-auto">
                        <button 
                            onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                            className="flex items-center justify-between w-full sm:w-auto gap-1 text-sm bg-[#3a3a3a] px-3 py-2 rounded"
                        >
                            <span className="flex items-center gap-1">
                                <span>$</span>
                                <span className="text-xs text-gray-400">{currency}</span>
                            </span>
                            <ChevronDown className="w-3 h-3" />
                        </button>
                        <AnimatePresence>
                            {showCurrencyDropdown && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    className="absolute left-0 top-full mt-1 bg-[#4a4a4a] rounded-lg shadow-xl z-10 w-full sm:w-32"
                                >
                                    {currencies.map((c) => (
                                        <button
                                            key={c}
                                            onClick={() => { setCurrency(c); setShowCurrencyDropdown(false); }}
                                            className="block w-full px-4 py-2 text-left text-sm text-white hover:bg-[#5a5a5a]"
                                        >
                                            {c}
                                        </button>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                    <input
                        type="text"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="flex-1 bg-transparent text-white text-base sm:text-lg focus:outline-none"
                        placeholder="0.00"
                    />
                </div>
                <p className="text-xs text-gray-400 mb-4">
                    Available funds to withdrawal: ${availableFunds} minimum withdrawal request is ${minWithdrawal}
                </p>

                {method === 'bank' && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-4"
                    >
                        <div className="space-y-2">
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="flex-1">
                                    <input
                                        type="text"
                                        value={accountName}
                                        onChange={handleAccountNameChange}
                                        placeholder="Bank Account Holders Full-Name"
                                        className={`w-full bg-[#4a4a4a] border rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#c9a227] text-sm sm:text-base ${
                                            accountNameError ? 'border-red-500' : 'border-[#5a5a5a]'
                                        }`}
                                        required
                                    />
                                    {accountNameError && (
                                        <p className="text-xs text-red-400 mt-1">{accountNameError}</p>
                                    )}
                                </div>
                                <div className="flex-1">
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        value={accountNumber}
                                        onChange={handleAccountNumberChange}
                                        onPaste={(e) => {
                                            const pastedText = e.clipboardData.getData('text');
                                            if (!/^\d+$/.test(pastedText)) {
                                                e.preventDefault();
                                            }
                                        }}
                                        placeholder="Enter Bank Account Number"
                                        className={`w-full bg-[#4a4a4a] border rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#c9a227] text-sm sm:text-base ${
                                            accountNumberError ? 'border-red-500' : 'border-[#5a5a5a]'
                                        }`}
                                        required
                                    />
                                    {accountNumberError && (
                                        <p className="text-xs text-red-400 mt-1">{accountNumberError}</p>
                                    )}
                                    {bank && accountNumber && !accountNumberError && accountNumber.length === getBankAccountLength(bank) && (
                                        <p className="text-xs text-green-400 mt-1">
                                            ✓ Valid {bank} account number
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Enhanced Bank Dropdown with Search */}
                        <div className="relative">
                            <button
                                onClick={() => setShowBankDropdown(!showBankDropdown)}
                                className="w-full sm:w-1/2 lg:w-1/3 bg-[#4a4a4a] border border-[#5a5a5a] rounded-lg p-3 text-left flex items-center justify-between focus:outline-none focus:border-[#c9a227]"
                            >
                                <span className={bank ? 'text-white text-sm sm:text-base' : 'text-gray-500 text-sm sm:text-base'}>
                                    {bank || 'Select Bank'}
                                </span>
                                <ChevronDown className="w-5 h-5 text-gray-400" />
                            </button>
                            
                            <AnimatePresence>
                                {showBankDropdown && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className="absolute left-0 top-full mt-1 w-full sm:w-1/2 lg:w-1/3 bg-[#4a4a4a] rounded-lg shadow-xl z-10"
                                    >
                                        <div className="p-2 border-b border-[#5a5a5a]">
                                            <input
                                                type="text"
                                                placeholder="Search bank..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="w-full bg-[#3a3a3a] text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-[#c9a227]"
                                                autoFocus
                                            />
                                        </div>
                                        <div className="max-h-48 overflow-y-auto">
                                            {filteredBanks.length > 0 ? (
                                                filteredBanks.map((b) => (
                                                    <button
                                                        key={b.code}
                                                        onClick={() => { 
                                                            setBank(b.name); 
                                                            setShowBankDropdown(false);
                                                            setSearchTerm('');
                                                        }}
                                                        className="block w-full px-4 py-2 text-left text-white hover:bg-[#5a5a5a] text-sm sm:text-base"
                                                    >
                                                        {b.name}
                                                    </button>
                                                ))
                                            ) : (
                                                <div className="px-4 py-2 text-gray-400 text-sm">
                                                    No banks found
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        <div className="flex justify-end">
                            <button
                                onClick={() => {
                                    // Final validation before proceeding
                                    const isNameValid = validateAccountName(accountName);
                                    const isNumberValid = validateAccountNumber(accountNumber, bank);
                                    
                                    if (isNameValid && isNumberValid && bank && accountName && accountNumber) {
                                        onComplete();
                                    } else {
                                        // Show error summary
                                        if (!bank) alert('Please select a bank');
                                        if (!accountName) alert('Please enter account name');
                                        if (!accountNumber) alert('Please enter account number');
                                    }
                                }}
                                className="w-full sm:w-auto px-6 py-3 bg-[#4a4a4a] hover:bg-[#5a5a5a] rounded-lg font-semibold transition-colors text-sm sm:text-base"
                                disabled={!!accountNameError || !!accountNumberError || !bank || !accountName || !accountNumber}
                            >
                                Request withdrawal
                            </button>
                        </div>
                    </motion.div>
                )}

                {method === 'paypal' && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-4"
                    >
                        <input
                            type="email"
                            placeholder="Enter your PayPal email address"
                            className="w-full bg-[#4a4a4a] border border-[#5a5a5a] rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#c9a227] text-sm sm:text-base"
                        />
                        <div className="flex justify-end">
                            <button
                                onClick={onComplete}
                                className="w-full sm:w-auto px-6 py-3 bg-[#4a4a4a] hover:bg-[#5a5a5a] rounded-lg font-semibold transition-colors text-sm sm:text-base"
                            >
                                Request withdrawal
                            </button>
                        </div>
                    </motion.div>
                )}

                {/* Warning Box */}
                <div className="bg-[#5c3a2a] border border-[#8b5a3a] rounded-lg p-3 sm:p-4 mt-4">
                    <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-[#c9a227] flex-shrink-0 mt-0.5" />
                        <div className="text-xs sm:text-sm text-gray-300 space-y-1">
                            <p className="font-semibold text-white text-sm sm:text-base">Review important instructions:</p>
                            <p>· Minimum withdrawal request is $50</p>
                            <p>· The rate for the withdrawal transaction is: {NGN_PER_USD} NGN = 1 USD</p>
                            {numericAmount > 0 && (
                                <p>
                                    · Breakdown: ${numericAmount.toFixed(2)} USD × {NGN_PER_USD} = ₦{nairaEquivalent.toLocaleString()} NGN
                                </p>
                            )}
                            <p>· Withdrawals might take 1-2 working days to process.</p>
                            <p>· Ensure your bank details are accurate before requesting a withdrawal to avoid delays.</p>
                            <p>· A small transaction fee may be deducted from your withdrawal amount.</p>
                            <p className="text-xs sm:text-sm">· Please note that you are able to withdraw only your Earnings, wallet top ups are not withdrawable.</p>
                        </div>
                    </div>
                </div>
            </div>
        </ModalWrapper>
    );
}